import { tileAt, type MapDef } from '../world/map';
import { T } from '../world/tiles';
import { fbm, hash2, hex, mix, noise2, scale, smoothstep, type RGB } from './noise';

/**
 * Ground materials of the hlcity package. Tile chars reserved for it: 'a', 'i' walkable, 'j' solid
 * (solidity is declared in src/world/tiles.ts). Runs inside the ground worker too,
 * so keep it pure: no DOM, no game state. Return null for chars you don't own.
 *
 * - 'a' coastal / riverside bike path: red-brown asphalt, white edge lines, dashed centre line;
 * - 'i' raked pale gravel with a few flat stones (Pine Garden, Qingxiu Temple);
 * - 'j' flooded paddy field: rows of rice tufts standing in sky-coloured water.
 */
const K = {
  bikeA: hex('#9a4a3c'),
  bikeB: hex('#b05a48'),
  paint: hex('#f2ece0'),
  gravelA: hex('#cfc7b6'),
  gravelB: hex('#e4ddcc'),
  stone: hex('#a9a296'),
  waterA: hex('#7fb8c4'),
  waterB: hex('#a8d4d8'),
  mud: hex('#6a5a40'),
  riceA: hex('#5fa83a'),
  riceB: hex('#8ccc4a'),
};

function bikePath(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  const is = (dx: number, dy: number) => tileAt(map, tx + dx, ty + dy) === 'a';
  let c = mix(K.bikeA, K.bikeB, fbm(x / 20, y / 20, 401));
  c = scale(c, 0.9 + hash2(Math.floor(x * 1.5), Math.floor(y * 1.5), 402) * 0.18);
  // White edge lines, 1.5 px in from the edge.
  if ((!is(-1, 0) && fx > 1.5 && fx < 2.5) || (!is(1, 0) && fx > T - 2.5 && fx < T - 1.5)) return K.paint;
  if ((!is(0, -1) && fy > 1.5 && fy < 2.5) || (!is(0, 1) && fy > T - 2.5 && fy < T - 1.5)) return K.paint;
  const horiz = is(-1, 0) && is(1, 0) && !(is(0, -1) && is(0, 1));
  const vert = is(0, -1) && is(0, 1) && !(is(-1, 0) && is(1, 0));
  // Dashed centre line between the two lanes.
  if (horiz && ((is(0, 1) && fy > T - 0.6) || (is(0, -1) && fy < 0.6)) && ((x % 24) + 24) % 24 < 12) return mix(K.paint, hex('#ffd34e'), 0.4);
  if (vert && ((is(1, 0) && fx > T - 0.6) || (is(-1, 0) && fx < 0.6)) && ((y % 24) + 24) % 24 < 12) return mix(K.paint, hex('#ffd34e'), 0.4);
  // A painted bicycle every so often on the vertical path.
  if (vert && is(1, 0) && ty % 9 === 4) {
    const u = fx - 8, v = fy - 8;
    const ring = (cx: number) => Math.abs(Math.hypot(u - cx, v + 1) - 2.6) < 0.5;
    if (ring(-3.4) || ring(3.4) || (Math.abs(v + 2.2 - (u + 3.4) * 0.35) < 0.4 && u > -3.4 && u < 3.4)) return K.paint;
  }
  return c;
}

function gravel(x: number, y: number): RGB {
  let c = mix(K.gravelA, K.gravelB, fbm(x / 18, y / 18, 411));
  const g = hash2(Math.floor(x * 1.8), Math.floor(y * 1.8), 412);
  if (g < 0.16) c = scale(c, 0.86);
  else if (g > 0.9) c = scale(c, 1.08);
  // Raked ripples.
  const rake = Math.sin(y * 1.3 + fbm(x / 30, y / 30, 413) * 3);
  if (rake > 0.82) c = scale(c, 0.92);
  // Flat stepping stones here and there.
  const sx = Math.floor(x / 22), sy = Math.floor(y / 22);
  if (hash2(sx, sy, 414) < 0.08) {
    const cx = (sx + 0.5) * 22, cy = (sy + 0.5) * 22;
    const d = Math.hypot((x - cx) / 7, (y - cy) / 5);
    if (d < 1) c = mix(K.stone, hex('#c4beb2'), (1 - d) * 0.6 + noise2(x, y, 415) * 0.2);
    else if (d < 1.15) c = scale(c, 0.8);
  }
  return c;
}

function paddy(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  // Muddy banks where the field meets the dykes.
  const edge = Math.min(
    tileAt(map, tx - 1, ty) !== 'j' ? fx : 99,
    tileAt(map, tx + 1, ty) !== 'j' ? T - fx : 99,
    tileAt(map, tx, ty - 1) !== 'j' ? fy : 99,
    tileAt(map, tx, ty + 1) !== 'j' ? T - fy : 99,
  ) + (noise2(x * 0.4, y * 0.4, 421) - 0.5) * 1.6;
  if (edge < 1.4) return mix(K.mud, hex('#4f7a2a'), noise2(x * 0.7, y * 0.7, 422) * 0.6);
  // Sky reflected in still water, with soft cloud patches.
  let c = mix(K.waterA, K.waterB, smoothstep(0.45, 0.7, fbm(x / 40, y / 26, 423)));
  if (edge < 3) c = mix(c, K.mud, 0.35);
  // Rice tufts in neat rows (planted by hand, slightly wobbly).
  const gx = 5, gy = 4;
  const cx = Math.floor(x / gx), cy = Math.floor(y / gy);
  const ox = (cx + 0.5) * gx + (hash2(cx, cy, 424) - 0.5) * 1.2, oy = (cy + 0.6) * gy;
  const u = x - ox, v = y - oy;
  if (v < 0.6 && v > -3.4 && Math.abs(u) < 1.6 - (v + 3.4) * -0.05 && Math.abs(u) < (v + 3.6) * 0.5) {
    const tone = hash2(cx, cy, 425);
    return mix(K.riceA, K.riceB, tone * 0.6 + (v < -2 ? 0.4 : 0));
  }
  // The tuft's reflection.
  if (v > 0.6 && v < 2 && Math.abs(u) < 0.8) return mix(c, K.riceA, 0.35);
  // Ripples.
  if (Math.sin((x + y * 0.3) * 0.9 + fbm(x / 9, y / 9, 426) * 6) > 0.97) c = mix(c, hex('#ffffff'), 0.35);
  return c;
}

/** Hualien Station's plaza (tile rect): big granite slabs with painted bays, a tactile strip and puddles. */
const PLAZA: readonly [number, number, number, number] = [1, 12, 34, 9];
const inPlaza = (tx: number, ty: number) => tx >= PLAZA[0] && ty >= PLAZA[1] && tx < PLAZA[0] + PLAZA[2] && ty < PLAZA[1] + PLAZA[3];

function plaza(x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  // 24 px polished slabs in two greys, laid in a running bond.
  const s = 24;
  const row = Math.floor(y / s), off = (row % 2) * 12;
  const col = Math.floor((x + off) / s);
  const u = x + off - col * s, v = y - row * s;
  const tone = hash2(col, row, 431);
  let c = mix(hex('#c4bfb6'), hex('#dcd6cc'), tone);
  if (tone > 0.82) c = mix(c, hex('#a9a9a4'), 0.5); // the odd darker granite slab
  c = scale(c, 0.95 + noise2(x * 0.5, y * 0.5, 432) * 0.1);
  // Puddles after the morning wash-down: darker, with the sky in them.
  const wet = fbm(x / 26, y / 14, 433);
  if (wet > 0.66) c = mix(scale(c, 0.78), hex('#9ec4d8'), smoothstep(0.66, 0.8, wet) * 0.55);
  if (u < 0.8 || v < 0.8) return mix(c, hex('#8a8580'), 0.8);
  // Gum spots and wear.
  if (hash2(Math.floor(x / 3), Math.floor(y / 3), 434) < 0.012) c = scale(c, 0.8);
  // Yellow tactile paving along the station front (first row of the plaza).
  if (ty === PLAZA[1] && fy > 4 && fy < 11) {
    const dot = Math.hypot(((x % 4) + 4) % 4 - 2, ((y % 4) + 4) % 4 - 2) < 1.2;
    return dot ? hex('#e6b800') : hex('#ffd34e');
  }
  // Taxi bay: yellow zig-zag on the west end of the kerb row; bus bay: a white box with a dashed edge.
  const bottom = PLAZA[1] + PLAZA[3] - 1;
  if (ty === bottom || ty === bottom - 1) {
    if (tx >= 1 && tx <= 12) {
      const zig = Math.abs((((x + (ty === bottom ? 0 : 8)) % 16) + 16) % 16 - 8) * 0.5;
      if (ty === bottom && fy > 11 && Math.abs(fy - 12.5 - zig * 0.4) < 0.9) return hex('#f2c230');
      if (ty === bottom - 1 && fy > 2 && fy < 3.4 && ((x % 10) + 10) % 10 < 6) return hex('#f2c230');
    }
    if (tx >= 16 && tx <= 30) {
      if (ty === bottom && fy > 13.4) return hex('#f2ece0');
      if ((tx === 16 && fx < 1.6) || (tx === 30 && fx > T - 1.6)) return hex('#f2ece0');
      if (ty === bottom - 1 && fy < 1.6 && ((x % 12) + 12) % 12 < 7) return hex('#f2ece0');
      // The word-free bus pictogram: a white rounded box with two wheels, once per bay.
      if (ty === bottom - 1 && (tx === 20 || tx === 26)) {
        const bus = fx > 3 && fx < 13 && fy > 5 && fy < 11;
        const wheel = Math.hypot(fx - 5.5, fy - 12) < 1.4 || Math.hypot(fx - 10.5, fy - 12) < 1.4;
        if (bus || wheel) return hex('#f2ece0');
      }
    }
  }
  // Direction arrows towards the exits on the middle row.
  if (ty === PLAZA[1] + 4 && (tx === 5 || tx === 29)) {
    const dir = tx === 5 ? -1 : 1;
    const ax = (fx - 8) * dir;
    if (fy > 6.4 && fy < 9.6 && ax > -5 && ax < 3) return hex('#f2ece0');
    if (ax >= 3 && ax < 7 && Math.abs(fy - 8) < 7 - ax) return hex('#f2ece0');
  }
  return c;
}

export function colorAtHlcity(map: MapDef, x: number, y: number, mat: string): RGB | null {
  switch (mat) {
    case 'a':
      return bikePath(map, x, y);
    case 'i':
      return map.id === 'hlcity' && inPlaza(Math.floor(x / T), Math.floor(y / T)) ? plaza(x, y) : gravel(x, y);
    case 'j':
      return paddy(map, x, y);
    default:
      return null;
  }
}

/** Flat colours shown while the detailed ground is still rendering. */
export const PLACEHOLDER_HLCITY: Record<string, string> = { a: '#a5523f', i: '#d8d1c0', j: '#86bcc0' };
